import Image from 'next/image'
import styled from 'styled-components'
import aiEditImg from '../../../assets/homepage-v2/ai-edit-card.png'
import aiRefineImg from '../../../assets/homepage-v2/ai-refine-card.png'
import aiRoomDesignerImg from '../../../assets/homepage-v2/ai-room-designer-card.png'
import unlimitedImg from '../../../assets/homepage-v2/unlimited-card.png'

const SectionWrapper = styled.section`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 80px 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 40px 0;
  }
`

const SectionContent = styled.div`
  width: 1200px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 26px;

  @media (max-width: 768px) {
    gap: 20px;
  }
`

const SectionTitle = styled.h2`
  color: black;
  font-size: 48px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  text-align: center;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
    padding: 0 20px;
  }
`

const SectionSubtitle = styled.p`
  color: #3f3f3f;
  font-size: 20px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  text-align: center;
  margin: 0;
  max-width: 900px;

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 0 20px;
  }
`

const CardsGrid = styled.div`
  width: 100%;
  height: 950px;
  position: relative;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    height: auto;
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding: 0 20px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &::after {
      content: '';
      flex-shrink: 0;
      width: 10px;
    }
  }
`

// Base card styles
const AICard = styled.div`
  position: absolute;
  border-radius: 20px;
  overflow: hidden;

  @media (max-width: 1024px) {
    position: relative;
    width: 321px;
    height: 389px;
    flex-shrink: 0;
  }
`

const CardContent = styled.div`
  position: absolute;
  left: 32px;
  top: 29px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 2;

  @media (max-width: 1024px) {
    left: 20px;
    top: 22px;
    gap: 15px;
    width: 268px;
  }
`

const CardTitle = styled.h3`
  color: white;
  font-size: 32px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  margin: 0;

  @media (max-width: 1024px) {
    font-size: 20px;
  }
`

const CardDescription = styled.p`
  color: white;
  font-size: 18px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  margin: 0;
  max-width: 340px;

  @media (max-width: 1024px) {
    font-size: 14px;
    max-width: 268px;
  }
`

const CardButton = styled.button`
  background: white;
  border: none;
  border-radius: 100px;
  height: 44px;
  padding: 0 32px;
  cursor: pointer;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
  width: fit-content;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 1024px) {
    height: 34px;
    width: 210px;
    padding: 0;
  }
`

const ButtonText = styled.span`
  color: black;
  font-size: 18px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;

  @media (max-width: 1024px) {
    font-size: 14px;
  }
`

// Card 1: AI Room Designer (Top-left, 780x465)
const AIRoomDesignerCard = styled(AICard)`
  left: 0;
  top: 0;
  width: 780px;
  height: 465px;
  background: linear-gradient(180deg, #8000db 0%, #ff36fc 100%);

  @media (max-width: 1024px) {
    width: 321px;
    height: 389px;
    left: auto;
    top: auto;
  }
`

// Full width, top 66px on desktop, top 90px on mobile, extends to bottom
const RoomDesignerImageArea = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 66px;
  bottom: 0;
  overflow: hidden;

  @media (max-width: 1024px) {
    top: 90px;
  }
`

const PromptInput = styled.div`
  position: absolute;
  left: 188px;
  top: 332px;
  width: 403px;
  height: 50px;
  background: white;
  border-radius: 10px;
  border: 1.5px solid #8100db;
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 4px;
  z-index: 2;

  @media (max-width: 1024px) {
    left: 6px;
    top: 306px;
    width: 309px;
    height: 42px;
  }
`

const SparkleText = styled.span`
  color: #8201db;
  font-size: 18px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-style: italic;
  font-weight: 300;

  @media (max-width: 1024px) {
    font-size: 14px;
  }
`

const PromptText = styled.span`
  color: #545454;
  font-size: 18px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-style: italic;
  font-weight: 300;

  @media (max-width: 1024px) {
    font-size: 14px;
  }
`

// Card 2: AI Edit Your Room (Top-right, 400x465)
const AIEditCard = styled(AICard)`
  left: 800px;
  top: 0;
  width: 400px;
  height: 465px;
  background: linear-gradient(180deg, #f78c8c 0%, #fbcfcf 100%);

  @media (max-width: 1024px) {
    width: 321px;
    height: 389px;
    left: auto;
    top: auto;
    background: linear-gradient(180deg, #f68c8c 0%, #f8abab 50%, #facdcd 100%);
  }
`

// Desktop: 367x234, left 16px, top 212px
// Mobile: 292x186, left 14px, top 174px to bottom
const EditImageArea = styled.div`
  position: absolute;
  left: 16px;
  top: 212px;
  width: 367px;
  height: 234px;
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: 1024px) {
    left: 14px;
    top: 174px;
    width: 292px;
    height: auto;
    bottom: 0;
  }
`

// Card 3: AI Refine Your Design (Bottom-left, 400x465)
const AIRefineCard = styled(AICard)`
  left: 0;
  top: 485px;
  width: 400px;
  height: 465px;
  background: linear-gradient(180deg, #ff2b00 0%, #ffc183 100%);

  @media (max-width: 1024px) {
    width: 321px;
    height: 389px;
    left: auto;
    top: auto;
    background: linear-gradient(180deg, #ff2d03 0%, #ff6d3a 50%, #ffbf81 100%);
  }
`

// Desktop: 373x234, left 14px, top 213px
// Mobile: 292x195, left 14px, top 168px to bottom
const RefineImageArea = styled.div`
  position: absolute;
  left: 14px;
  top: 213px;
  width: 373px;
  height: 234px;
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: 1024px) {
    left: 14px;
    top: 168px;
    width: 292px;
    height: auto;
    bottom: 0;
  }
`

// Card 4: Unlimited AI Creativity (Bottom-right, 780x465)
const AICreativityCard = styled(AICard)`
  left: 420px;
  top: 485px;
  width: 780px;
  height: 465px;
  background: linear-gradient(180deg, #0090b8 0%, #b8f2f5 100%);

  @media (max-width: 1024px) {
    width: 321px;
    height: 389px;
    left: auto;
    top: auto;
  }
`

// Desktop: full width, top 193px to bottom
// Mobile: extends beyond card, left -78px, top 176px to bottom
const CreativityImageArea = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 193px;
  bottom: 0;
  overflow: hidden;

  @media (max-width: 1024px) {
    left: -78px;
    top: 176px;
    width: 470px;
    right: auto;
  }
`

const AIFeaturesSection = ({ onActionClick }) => {
  return (
    <SectionWrapper>
      <SectionContent>
        <SectionTitle>Redesign Your Room with AI.</SectionTitle>
        <SectionSubtitle>
          Instantly transform your space with intelligent AI tools. Explore new styles, refine
          layouts, and visualize interior design ideas in seconds — no experience needed.
        </SectionSubtitle>

        <CardsGrid>
          {/* Card 1: AI Room Designer (Top-left, 780x465) */}
          <AIRoomDesignerCard>
            <CardContent>
              <CardTitle>AI Room Designer</CardTitle>
              <CardDescription>
                Generate complete room designs in seconds. Instantly explore layouts, furniture,
                colors, and décor for your space.
              </CardDescription>
              <CardButton onClick={onActionClick}>
                <ButtonText>Design with AI</ButtonText>
              </CardButton>
            </CardContent>
            <RoomDesignerImageArea>
              <Image
                src={aiRoomDesignerImg}
                alt="AI Room Designer preview"
                layout="fill"
                objectFit="cover"
              />
            </RoomDesignerImageArea>
            <PromptInput>
              <SparkleText>.&#10022;&#11089;</SparkleText>
              <PromptText>Warm modern living room with natural light</PromptText>
            </PromptInput>
          </AIRoomDesignerCard>

          {/* Card 2: AI Edit Your Room (Top-right, 400x465) */}
          <AIEditCard>
            <CardContent>
              <CardTitle>AI Edit Your Room</CardTitle>
              <CardDescription>
                Instantly swap furniture, décor, colors, or materials in your room using AI.
              </CardDescription>
              <CardButton onClick={onActionClick}>
                <ButtonText>Edit with AI</ButtonText>
              </CardButton>
            </CardContent>
            <EditImageArea>
              <Image
                src={aiEditImg}
                alt="AI Edit Your Room preview"
                layout="fill"
                objectFit="contain"
              />
            </EditImageArea>
          </AIEditCard>

          {/* Card 3: AI Refine Your Design (Bottom-left, 400x465) */}
          <AIRefineCard>
            <CardContent>
              <CardTitle>AI Refine Your Design</CardTitle>
              <CardDescription>
                Fine-tune layout balance, lighting, mood, and realism with one click.
              </CardDescription>
              <CardButton onClick={onActionClick}>
                <ButtonText>Refine My Design</ButtonText>
              </CardButton>
            </CardContent>
            <RefineImageArea>
              <Image
                src={aiRefineImg}
                alt="AI Refine Your Design preview"
                layout="fill"
                objectFit="contain"
              />
            </RefineImageArea>
          </AIRefineCard>

          {/* Card 4: Unlimited AI Creativity (Bottom-right, 780x465) */}
          <AICreativityCard>
            <CardContent style={{ left: 36 }}>
              <CardTitle>Unlimited AI Creativity</CardTitle>
              <CardDescription style={{ maxWidth: 368 }}>
                Design anything you imagine. Explore interior styles, color palettes, layouts, and
                home décor trends.
              </CardDescription>
              <CardButton onClick={onActionClick}>
                <ButtonText>Imagine with AI</ButtonText>
              </CardButton>
            </CardContent>
            <CreativityImageArea>
              <Image
                src={unlimitedImg}
                alt="Unlimited AI Creativity preview"
                layout="fill"
                objectFit="contain"
              />
            </CreativityImageArea>
          </AICreativityCard>
        </CardsGrid>
      </SectionContent>
    </SectionWrapper>
  )
}

export default AIFeaturesSection
